import { useState, useEffect, useMemo } from "react";
import { RenderMarkdownWithLatex } from "../../utils/utils";
import SolutionStepper from "../molecules/SolutionStepper";
import HintAndFormulas from "../molecules/HintAndFormulas";

const apiUrl = process.env.REACT_APP_API_SOURCE;
const GROUPS_PER_PAGE = 5;
const NO_CKE = "none";

const CAT_COLORS = {
  A_NA_OTWARTE: "bg-green-100 text-green-800 border-green-300",
  B_ROZBITE: "bg-orange-100 text-orange-800 border-orange-300",
  B_PRZEDZIALAMI_ODLOZONE: "bg-orange-50 text-orange-900 border-dashed border-orange-400",
  C_FRAGMENT_1PKT: "bg-yellow-100 text-yellow-800 border-yellow-300",
  K_ZAMKNIETE_OK: "bg-sky-100 text-sky-800 border-sky-300",
  N_NIEPEWNE: "bg-purple-100 text-purple-800 border-purple-300",
  X_BLAD_DANYCH: "bg-rose-200 text-rose-900 border-rose-400",
  CZYSCIEC_DO_A: "bg-lime-50 text-lime-900 border-dashed border-lime-500",
  CZYSCIEC_DO_K: "bg-cyan-50 text-cyan-900 border-dashed border-cyan-500",
  E_ZA_PROSTE: "bg-stone-200 text-stone-700 border-stone-400",
  Z_NIEOCENIONE: "bg-white text-slate-700 border-dashed border-slate-400",
  Y_BEZ_CKE: "bg-slate-100 text-slate-700 border-slate-300",
};

const Block = ({ label, children, tone = "gray" }) => {
  const tones = {
    gray: "border-gray-200 bg-gray-50",
    blue: "border-blue-200 bg-blue-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    green: "border-green-200 bg-green-50/50",
  };
  return (
    <div className={`mt-2 border rounded-md ${tones[tone]}`}>
      <div className="px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-inherit">{label}</div>
      <div className="px-3 py-2 text-sm text-gray-900 leading-relaxed">{children}</div>
    </div>
  );
};

const CkeHeader = ({ m }) => (
  <div className="bg-white border-2 border-blue-300 rounded-lg p-4">
    <div className="flex flex-wrap items-baseline gap-3">
      <h2 className="text-lg font-bold text-blue-900">{m.source}</h2>
      <span className="text-sm font-bold px-2 py-0.5 rounded bg-blue-900 text-white">{m.points} pkt</span>
      <span className="text-sm text-gray-600">{m.is_open ? "otwarte / wielopunktowe" : "zamknięte"}</span>
    </div>
    <Block label="Treść CKE" tone="blue"><RenderMarkdownWithLatex text={m.question || "Brak treści."} /></Block>
    {m.reference_solution && (
      <Block label="Rozwiązanie CKE" tone="green"><RenderMarkdownWithLatex text={m.reference_solution} /></Block>
    )}
    {m.grading_criteria && (
      <Block label="Kryteria oceniania" tone="amber"><RenderMarkdownWithLatex text={m.grading_criteria} /></Block>
    )}
  </div>
);

const ProblemCard = ({ row, matura, groupId }) => {
  const own = row.links.find(l => String(l.matura_problem_id) === groupId);
  const others = row.links.filter(l => String(l.matura_problem_id) !== groupId);
  const ckePoints = matura[groupId]?.points;

  return (
    <div className="bg-white rounded-lg shadow p-4 mt-3 ml-4 border-l-4 border-gray-300">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-bold text-gray-800">#{row.problem_id}</span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${CAT_COLORS[row.kategoria]}`}>{row.kategoria}</span>
        {!row.is_active && <span className="text-xs px-2 py-0.5 rounded bg-gray-200 text-gray-600">nieaktywne</span>}
        {own && (
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
            ckePoints != null && own.points_assigned !== ckePoints ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"}`}>
            u nas {own.points_assigned} pkt / CKE {ckePoints} pkt
          </span>
        )}
        <span className="text-xs text-gray-500">{row.dzial} → {row.podtemat}</span>
      </div>
      {others.length > 0 && (
        <div className="text-xs text-gray-500 mt-1">
          Podpięte też pod: {others.map(l => `${matura[l.matura_problem_id]?.source} (${l.points_assigned})`).join(", ")}
        </div>
      )}
      {row.arkusz && <div className="text-xs text-blue-700 mt-1">Arkusz: {row.arkusz}</div>}
      {row.uwagi && <div className="text-xs text-red-700 bg-red-50 rounded px-2 py-1 mt-1">{row.uwagi}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3 items-start">
        <AppPreview row={row} points={own?.points_assigned} />
        <div className="bg-slate-50 rounded-xl p-3">
          <HintAndFormulas hintText={row.hint} formulas={row.linked_formulas} />
          <SolutionStepper solutionText={row.solution || "Brak rozwiązania."} />
        </div>
      </div>
    </div>
  );
};

const imageSrc = link => link.startsWith("http")
  ? link
  : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ""}/images/${link}`;

const AppPreview = ({ row, points }) => (
  <div className="w-full max-w-md mx-auto bg-white rounded-2xl border border-gray-200 shadow-md p-4">
    <p className="text-base font-normal text-gray-900 text-center min-h-[50px] flex items-center justify-center">
      <RenderMarkdownWithLatex text={points ? `(${points} pkt) ${row.question || ""}` : (row.question || "_(pusta treść)_")} />
    </p>
    {row.image_link && (
      <div className="flex justify-center py-2">
        <img src={imageSrc(row.image_link)} alt="Ilustracja zadania" className="max-w-full h-auto rounded border" />
      </div>
    )}
    <div className="grid grid-cols-1 gap-2 mt-3">
      {row.answers.filter(Boolean).map((a, i) => (
        <div key={i} className={`w-full px-4 py-3 rounded-xl border-2 text-center text-sm ${
          i === 0 ? "border-green-500 bg-green-50 text-green-900 font-medium" : "border-gray-200 bg-white text-gray-800"}`}>
          <RenderMarkdownWithLatex text={a} />
        </div>
      ))}
    </div>
  </div>
);

// "1788-1807, 918-925 9076" -> Set id; null, gdy zapytanie nie jest listą ID
const parseIds = q => {
  if (!/^[\d\s,#-]+$/.test(q) || !/\d/.test(q)) return null;
  const ids = new Set();
  q.replace(/#/g, "").split(/[\s,]+/).filter(Boolean).forEach(part => {
    const [a, b] = part.split("-").map(Number);
    for (let i = a; i <= (b || a); i++) ids.add(i);
  });
  return ids;
};

const PPOpenAuditAdminPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cats, setCats] = useState(new Set());
  const [query, setQuery] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetch(`${apiUrl}pp-open-audit/`, { headers: { Authorization: `Token ${localStorage.getItem("token")}` } })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setData)
      .catch(e => setError(`Nie udało się pobrać audytu (${e.message}).`));
  }, []);

  const counts = useMemo(() => {
    const c = {};
    (data?.rows || []).forEach(r => {
      if (!onlyActive || r.is_active) c[r.kategoria] = (c[r.kategoria] || 0) + 1;
    });
    return c;
  }, [data, onlyActive]);

  const groups = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const idSet = parseIds(q);
    const byCke = {};
    data.rows.forEach(r => {
      if (cats.size && !cats.has(r.kategoria)) return;
      if (onlyActive && !r.is_active) return;
      const key = r.primary_matura_id != null ? String(r.primary_matura_id) : NO_CKE;
      const m = data.matura[key];
      if (idSet) {
        if (!idSet.has(r.problem_id)) return;
      } else if (q && !(r.question || "").toLowerCase().includes(q) &&
        !(r.podtemat || "").toLowerCase().includes(q) && !(m?.source || "").toLowerCase().includes(q)) return;
      (byCke[key] = byCke[key] || []).push(r);
    });
    return Object.entries(byCke)
      .map(([id, rows]) => ({ id, rows, source: data.matura[id]?.source || "Bez zadania CKE" }))
      .sort((a, b) => a.source.localeCompare(b.source, "pl", { numeric: true }));
  }, [data, cats, query, onlyActive]);

  useEffect(() => setPage(1), [cats, query, onlyActive]);

  const toggleCat = key => setCats(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const total = groups.reduce((s, g) => s + g.rows.length, 0);
  const pages = Math.max(1, Math.ceil(groups.length / GROUPS_PER_PAGE));
  const visible = groups.slice((page - 1) * GROUPS_PER_PAGE, page * GROUPS_PER_PAGE);
  const goTo = p => { setPage(p); window.scrollTo(0, 0); };

  const Pager = () => pages > 1 && (
    <div className="flex items-center justify-between my-4">
      <button disabled={page === 1} onClick={() => goTo(page - 1)}
        className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 disabled:opacity-40">← Poprzednia</button>
      <span className="text-sm text-gray-500">Strona {page} / {pages}</span>
      <button disabled={page === pages} onClick={() => goTo(page + 1)}
        className="bg-gray-200 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-300 disabled:opacity-40">Następna →</button>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-6 bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold mb-1 text-gray-800">Audyt zadań PP</h1>
      <p className="text-sm text-gray-500 mb-4">
        Cel: tylko pełne zadania typu maturalnego, za tyle punktów co zadanie CKE. Bez zadań is_mobile.
        Zadania pogrupowane pod zadaniem CKE, z którego są zrobione.
      </p>

      {error && <p className="text-red-600 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">{error}</p>}
      {!data && !error && <p className="text-blue-600 text-center py-4">Ładowanie (liczy się z bazy, chwilę to trwa)...</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
            {data.categories.filter(c => counts[c.key] || cats.has(c.key)).map(c => (
              <button key={c.key} onClick={() => toggleCat(c.key)}
                className={`text-left p-3 rounded-lg border-2 transition ${CAT_COLORS[c.key]} ${
                  cats.has(c.key) ? "ring-2 ring-gray-800" : "opacity-80 hover:opacity-100"}`}>
                <div className="flex justify-between font-bold text-sm">
                  <span>{c.key}</span><span>{counts[c.key] || 0}</span>
                </div>
                <div className="text-xs mt-1 leading-snug">{c.opis}</div>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center bg-white p-3 rounded-lg shadow sticky top-0 z-10">
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="ID / zakresy (1788-1807, 918-925), fragment treści, podtemat albo źródło CKE (np. 'Maj 2025')"
              className="border rounded px-3 py-1.5 text-sm flex-1 min-w-[240px]" />
            <label className="text-sm flex items-center gap-1">
              <input type="checkbox" checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)} /> tylko aktywne
            </label>
            {(cats.size > 0 || query) && (
              <button onClick={() => { setCats(new Set()); setQuery(""); }}
                className="text-xs px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 font-semibold">Wyczyść</button>
            )}
            <span className="text-sm text-gray-600 ml-auto">
              <b>{groups.length}</b> zadań CKE · <b>{total}</b> naszych zadań
            </span>
          </div>

          <Pager />
          {visible.map(g => (
            <section key={g.id} className="mb-10">
              {data.matura[g.id]
                ? <CkeHeader m={data.matura[g.id]} />
                : <div className="bg-white border-2 border-gray-300 rounded-lg p-4 font-bold text-gray-700">Bez zadania CKE</div>}
              <div className="text-sm text-gray-600 mt-2 ml-4">Nasze zadania pod tym zadaniem: <b>{g.rows.length}</b></div>
              {g.rows.map(r => <ProblemCard key={r.problem_id} row={r} matura={data.matura} groupId={g.id} />)}
            </section>
          ))}
          <Pager />
        </>
      )}
    </div>
  );
};

export default PPOpenAuditAdminPage;
